import React, {useEffect, useState} from 'react';
import {Alert, Button, Checkbox, Form, InputNumber, message, Select, Space, Switch, Tag} from "antd";
import {WarningOutlined} from "@ant-design/icons";
import { useTranslation } from '../../hooks/useTranslation';
import { isEnglishSite } from '../../config/site';
import { useUserInfo } from '../../contexts/UserInfoContext';
import type { ImgCloudUserConfig } from '../../services/userInfo';

export interface HostOption {
  value: string;
  label: string;
}

export interface ImgCloudSettingsUpdateRequest {
  host?: string;
  lifecycleDays: string;
  enableUploadPrewarm?: boolean;
}

// 兜底域名选项（当远程获取失败时使用）
export const FALLBACK_HOST_OPTIONS: HostOption[] = [
  { value: 'tc-new.z.wiki', label: 'tc-new.z.wiki (国内域名，推荐)' },
  { value: 'img.51shazhu.com', label: 'img.51shazhu.com（国内域名，推荐）' },
  { value: 'a.tuchuangyun.top', label: 'a.tuchuangyun.top (国外节点，推荐)' },
  { value: 'xland.eu.org', label: 'xland.eu.org (国外很快，推荐亚马逊使用)' },
  { value: 'jp.tuchuangyun.top', label: 'jp.tuchuangyun.top (仅国外可访问，不推荐)' },
  { value: 'edgeone.51shazhu.com', label: 'edgeone.51shazhu.com（不推荐)' },
  { value: 'dl.z.wiki', label: 'dl.z.wiki (不推荐)' },
  { value: 'tc.z.wiki', label: 'tc.z.wiki (不推荐)' },
  { value: 'y.z.wiki', label: 'y.z.wiki (阿里云节点，不推荐)' },
];

const UPLOAD_HOST_OPTIONS_ENDPOINT = '/config/upload-host-options';
const MAX_LIFECYCLE_DAYS = 36500;

export const normalizeHostOptions = (rawOptions: unknown): HostOption[] => {
  if (!Array.isArray(rawOptions)) {
    return [];
  }

  const seen = new Set<string>();
  return rawOptions.reduce<HostOption[]>((options, item) => {
    let value = '';
    let label = '';

    if (typeof item === 'string') {
      value = item.trim();
      label = value;
    } else if (item && typeof item === 'object') {
      const option = item as { value?: unknown; label?: unknown };
      value = typeof option.value === 'string' ? option.value.trim() : '';
      label = typeof option.label === 'string' ? option.label.trim() : '';
    }

    if (!value || seen.has(value)) {
      return options;
    }

    seen.add(value);
    options.push({ value, label: label || value });
    return options;
  }, []);
};

export const mergeHostOptions = (defaultOptions: HostOption[], userDomains: unknown): HostOption[] => {
  const userDomainOptions = Array.isArray(userDomains)
    ? userDomains
      .filter((domain): domain is string => typeof domain === 'string')
      .map((domain) => ({ value: domain, label: domain }))
    : [];
  return normalizeHostOptions([...defaultOptions, ...userDomainOptions]);
};

export const loadUploadHostOptions = async (): Promise<HostOption[]> => {
  try {
    const res = await fetch(UPLOAD_HOST_OPTIONS_ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin',
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const result = await res.json();
    if (result?.success === false) {
      throw new Error(result?.message || 'upload host options disabled');
    }

    const options = normalizeHostOptions(result?.data || result);
    if (options.length > 0) {
      console.info('成功获取域名开关配置', options);
      return options;
    }
    console.warn('域名开关配置为空或格式不正确，使用兜底数据');
  } catch (error) {
    console.error('获取域名开关配置失败，使用兜底数据', error);
  }
  return FALLBACK_HOST_OPTIONS;
};

export const isEnabledConfigValue = (value: unknown): boolean => (
  value === true || (typeof value === 'string' && value.toLowerCase() === 'true')
);

export const buildSettingsUpdateRequest = (
  config: ImgCloudUserConfig,
  lifecycleDays: number | null,
  isActiveVip: boolean,
  enableUploadPrewarm: boolean,
): ImgCloudSettingsUpdateRequest => ({
  ...(config.host ? { host: config.host } : {}),
  lifecycleDays: lifecycleDays?.toString() || '',
  ...(isActiveVip ? { enableUploadPrewarm } : {}),
});

const Config: React.FC = () => {
  const { t, currentLanguage, changeLanguage } = useTranslation();
  const {
    config: sharedConfig,
    isActiveVip,
    isAuthenticated: isLoggedIn,
    refresh,
    status,
    user,
  } = useUserInfo();
  const [config, setConfig] = useState<ImgCloudUserConfig>({});
  const [bigFileUpload, setBigFileUpload] = useState(false);
  const [lifecycleDays, setLifecycleDays] = useState<number | null>(null);
  const [hostOptions, setHostOptions] = useState<HostOption[]>([]);
  const [enableUploadPrewarm, setEnableUploadPrewarm] = useState(false);

  // 域名开关独立加载，用户域名直接来自共享用户快照。
  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      const domains = await loadUploadHostOptions();
      if (active) {
        setHostOptions(mergeHostOptions(domains, user?.domains || []));
      }
    };
    void fetchData();
    return () => {
      active = false;
    };
  }, [user?.domains]);

  useEffect(() => {
    if (status !== 'authenticated') {
      return;
    }
    setConfig({ ...sharedConfig });
    setLifecycleDays(sharedConfig.lifecycleDays ? parseInt(sharedConfig.lifecycleDays, 10) : null);
    setEnableUploadPrewarm(isEnabledConfigValue(sharedConfig.enableUploadPrewarm));
  }, [sharedConfig, status]);

  const [saving, setSaving] = useState(false)

  const handleLanguageChange = async (value: 'zh' | 'en') => {
    await changeLanguage(value);
    // 语言切换后刷新页面以应用所有更改
    window.location.reload();
  };

  const handleSave = () => {
    console.info('handleSave', config)
    if (
      lifecycleDays !== null &&
      (!Number.isInteger(lifecycleDays) || lifecycleDays < 1 || lifecycleDays > MAX_LIFECYCLE_DAYS)
    ) {
      message.error(t('config.lifecycleInvalid'));
      return;
    }

    setSaving(true);
    
    // 更新配置对象包含生命周期天数
    const updatedConfig = buildSettingsUpdateRequest(
      config,
      lifecycleDays,
      isActiveVip,
      enableUploadPrewarm,
    );
    
    fetch('/admin/config/update', {
      method: 'POST',
      body: JSON.stringify(updatedConfig),
      headers: {
        'Content-Type': 'application/json;charset=utf8'
      }
    }).then(res => res.json()).then((data) => {
      console.info('flag is', data);
      if (data.success) {
        message.info(t('config.configUpdated'))
        void refresh();
      } else {
        message.error(data.message || t('config.configUpdateFailed'))
      }
    }).finally(() => {
      setSaving(false);
    })
  }

  return (
    <>
      <Form>
        {/* 语言选择 - 英文站（postanything.net）不展示 */}
        {!isEnglishSite() && (
          <Form.Item label={t('config.language')}>
            <Select
              placeholder={t('config.languagePlaceholder')}
              value={currentLanguage}
              onChange={handleLanguageChange}
              style={{ width: '100%' }}
              options={[
                { value: 'zh', label: t('config.chinese') },
                { value: 'en', label: t('config.english') },
              ]}
            />
          </Form.Item>
        )}

        {/* VIP 相关配置 - 仅登录用户可见 */}
        {isLoggedIn && (
          <>
            <Alert message={t('config.vipOnly')} icon={<WarningOutlined/>} type="error" style={{marginBottom: '8px'}} showIcon/>

            {/* <Form.Item label="水印内容">
              <Input placeholder="请输入水印内容，最多20个字符串" value={config.watermask}
                     onChange={(e) => setConfig({...config, watermask: e.target.value.slice(0, 20)})}/>
            </Form.Item> */}
            {
              isActiveVip && (
                <Form.Item label={t('config.domainSelection')}>
                  <Select
                    placeholder={t('config.domainPlaceholder')}
                    value={config.host}
                    onChange={(value) => setConfig({...config, host: value})}
                    options={hostOptions}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              )
            }
            
            {
              isActiveVip && (
                <Form.Item label={t('config.lifecycleSettings')}>
                  <InputNumber
                    placeholder={t('config.lifecyclePlaceholder')}
                    min={1}
                    max={MAX_LIFECYCLE_DAYS}
                    precision={0}
                    value={lifecycleDays}
                    onChange={(value) => setLifecycleDays(value)}
                    addonAfter={t('common.days')}
                    style={{ width: '100%' }}
                  />
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    {t('config.lifecycleRangeHint')}
                  </div>
                  <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '4px' }}>
                    {t('config.lifecycleWarning')}
                  </div>
                </Form.Item>
              )
            }
            <Form.Item
              label={(
                <Space size={6}>
                  <span>{t('config.uploadPrewarm')}</span>
                  <Tag color="gold" style={{ marginInlineEnd: 0 }}>VIP</Tag>
                </Space>
              )}
            >
              <Switch
                checked={isActiveVip && enableUploadPrewarm}
                disabled={!isActiveVip}
                checkedChildren={t('config.open')}
                unCheckedChildren={t('config.closed')}
                onChange={setEnableUploadPrewarm}
              />
              <div style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
                {isActiveVip ? t('config.uploadPrewarmHint') : t('config.uploadPrewarmVipOnly')}
              </div>
            </Form.Item>
            {
              isEnabledConfigValue(config.allowJsUpload) && <Form.Item label={t('config.bigFileUpload')}>
                <Checkbox
                  checked={bigFileUpload}
                  onChange={(e) => {
                    console.info('e', e.target.checked, config)
                    setBigFileUpload(e.target.checked);
                    (window as any)._debug_ = e.target.checked;
                  }}
                >{t('config.open')}</Checkbox>
              </Form.Item>
            }

            {/* 保存按钮 - 仅登录用户可见 */}
            <Form.Item>
              <Button type="primary" loading={saving} onClick={handleSave}>{t('config.save')}</Button>
            </Form.Item>
          </>
        )}
      </Form>
    </>
  );
};

export default Config;
